import { NgClass, SlicePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DatePickerComponent } from 'components/input/date-time-picker/date-picker.component';
import { EditableContentComponent, EditableContentDisplayComponent, EditableContentEditComponent } from 'components/input/editable-content/editable-content.component';
import { EditableContentDirective } from 'components/input/editable-content/editable-content.directive';
import { IconComponent } from 'components/layout/icon/icon.component';
import { ViewTaskflowComponent } from 'components/task/taskflow/view-taskflow.component';
import { TaskActionType } from 'enums/TaskActionType';
import { TaskflowStatus } from 'enums/TaskflowStatus';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { TaskflowModel } from 'models/generated/Entities.GormCrm.Taskflow';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { UserPipe } from 'pipes/user.pipe';
import { NavigationService } from 'services/navigation.service';
import { TaskService } from 'services/task.service';
import { UserService } from 'services/user.service';

@Component({
	selector: 'taskflow-timeline',
	templateUrl: './taskflow-timeline.component.html',
	styleUrls: ['./taskflow-timeline.component.scss'],
	imports: [NgClass, EditableContentDirective, EditableContentComponent, EditableContentDisplayComponent, EditableContentEditComponent, IconComponent, DatePickerComponent, SlicePipe, TranslatePipe, CustomDatePipe, UserPipe]
})
export class TaskflowTimelineComponent {

	@Input('activeTaskAction') activeTaskAction?: TaskActionModel;
	@Output() activeTaskActionChange = new EventEmitter<TaskActionModel>();

	@Input('tentativeTaskAction') tentativeTaskAction?: TaskActionModel;
	@Output() createNewTaskAction = new EventEmitter<TaskActionModel>();

	private _taskflow = new TaskflowModel();
	@Input()
	set taskflow(taskflow: TaskflowModel) {
		this._taskflow = taskflow;
		this.buildBreadcrumbList(taskflow);
	}
	get taskflow() {
		return this._taskflow;
	}

	userIsOwner = false;
	taskflowStatusTypeEnum = TaskflowStatus;
	breadcrumbList: TaskflowModel[] = [];

	// Preserve the same array of steps across the component's life, to avoid refreshing existing HTML elements
	private _steps: { number: number, active: boolean, locked: boolean }[] = [];
	get steps() {
		this._steps.forEach(step => { step.locked = step.active = false; });

		for (let task of this.taskflow.taskActions) {
			if (!this._steps[task.step]) this._steps[task.step] = { number: task.step, locked: false, active: false };
			this._steps[task.step].locked = task.step > this.taskflow.currentStep;
			if (task === this.activeTaskAction) this._steps[task.step].active = true;
		}
		for (let subTaskflow of this.taskflow.subTaskflows) {
			const step = subTaskflow.stepOnParent!;
			if (!this._steps[step]) this._steps[step] = { number: step, locked: false, active: false };
			this._steps[step].locked = step > this.taskflow.currentStep;
		}
		return this._steps;
	}

	constructor(private userService: UserService, private navigationService: NavigationService, private readonly taskService: TaskService, private readonly view?: ViewTaskflowComponent) {
		this.userIsOwner = this.taskflow.owner === this.userService.getCurrentUser().id;
	}

	tasksInStep(step: number): TaskActionModel[] {
		return this.taskflow.taskActions.filter(action => action.step === step && !this.taskService.isTriggerTask(action))
			.sort((a, b) => { return (a.created ? new Date(a.created).getTime() : new Date(0).getTime()) - (b.created ? new Date(b.created).getTime() : new Date(0).getTime()); });
	}
	stepHasActiveTriggers(step: number): boolean {
		return this.taskflow.taskActions.some(action => action.step === step && this.taskService.isTriggerTask(action) && !action.completed);
	}
	subTasksflowsInStep(step: number): TaskflowModel[] {
		return this.taskflow.subTaskflows.filter(t => t.stepOnParent === step)
			.sort((a, b) => { return (a.created ? new Date(a.created).getTime() : new Date(0).getTime()) - (b.created ? new Date(b.created).getTime() : new Date(0).getTime()); });
	}

	taskType(type: number): string {
		return TaskActionType[type];
	}

	createNewTaskActionOnStep(step: number) {
		const newTaskAction = new TaskActionModel();
		newTaskAction.step = step;
		this.createNewTaskAction.emit(newTaskAction);
	}

	navigateToTaskAction(taskAction: TaskActionModel) {
		if (!taskAction.taskflow) return;
		if (taskAction.taskflow === this.taskflow) this.activeTaskActionChange.emit(taskAction);
		else this.navigateToTaskflow(taskAction.taskflow.id!, taskAction.id);
	}
	navigateToTaskflow(id: string, actionId?: string) {
		const navigationTask = this.view
			? this.navigationService.navigateView(this.view, ViewTaskflowComponent)
			: this.navigationService.refreshView(ViewTaskflowComponent);
		
		navigationTask.then(view => {
			if (actionId) view.selectedActionId = actionId;
			view.taskflowId = id!;
		});
	}
	
	buildBreadcrumbList(taskflow: TaskflowModel) {
		this.breadcrumbList = [];
		this.breadcrumbList.unshift(taskflow);
		while (taskflow.parentTaskflow != undefined) {
			taskflow = taskflow.parentTaskflow;
			this.breadcrumbList.unshift(taskflow);
		}
	}


	today = new Date(Date.now());
	yesterday = new Date(Date.now() - 1000 * 60 * 60 * 24);
	dateConflicts: DateConflict[] = [];
	private _markedDates: Date[] = [];


	get markedDates() {
		const dates: DateConflict[] = [];
		this.getConflictingDates(this.yesterday, this._taskflow, dates);
		this._markedDates = dates.map(c => c.date);
		return this._markedDates;
	}
	get suggestedDate() {
		const dates = this.markedDates;
		return dates.length > 0 ? dates.sort((a, b) => b.getTime() - a.getTime())[0] : null;
	}

	testDateConflicts(date: Date) {

		this.dateConflicts = [];
		this.getConflictingDates(date, this._taskflow, this.dateConflicts);
		this.matchEarlierDeadlines(date, this._taskflow, this.dateConflicts);
	}

	matchEarlierDeadlines(date: Date, taskflow: TaskflowModel, conflicts: DateConflict[]) {
		if (!taskflow.parentTaskflow) return;
		if (taskflow.parentTaskflow.deadline && this.getDateFrom(taskflow.parentTaskflow.deadline) < date) {
			conflicts.push({ taskflow: taskflow.parentTaskflow, date: this.getDateFrom(taskflow.parentTaskflow.deadline) });
		}
		this.matchEarlierDeadlines(date, taskflow.parentTaskflow, conflicts);
	}

	getConflictingDates(date: Date, taskflow: TaskflowModel, conflicts: DateConflict[]) {
		for (let i = 0; i < taskflow.taskActions.length; i++) {

			const action = taskflow.taskActions[i];
			if (action.plannedDate && this.getDateFrom(action.plannedDate) > date) {
				conflicts.push({ taskAction: action, date: this.getDateFrom(action.plannedDate) });
			}
		}
		for (let i = 0; i < taskflow.subTaskflows.length; i++) {
			if (taskflow.subTaskflows[i].deadline && this.getDateFrom(taskflow.subTaskflows[i].deadline!) > date) {
				conflicts.push({ taskflow: taskflow.subTaskflows[i], date: this.getDateFrom(taskflow.subTaskflows[i].deadline!) });
			}
			this.getConflictingDates(date, taskflow.subTaskflows[i], conflicts);
		}
	}

	getDateFrom(sourceDate: Date): Date {
		return new Date(new Date(sourceDate).toDateString());
	}
	updateDeadline(deadline: Date) {
		if (!this.userIsOwner) return;
		this.taskflow.deadline = deadline;
		this.taskService.updateTaskflowDeadline(this.taskflow.id!, deadline);
	}
	
}
class DateConflict {
	taskflow?: TaskflowModel;
	taskAction?: TaskActionModel;
	date!: Date;
}
